import React, { useState, useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import Purchase from './Purchase';
import viewAnalytics from './viewAnalytics';
import ProductImages from './ProductImages';
import Top from '../Top/Top';
import TemplateParser from '../Templates/TemplateParser';
import Breadcrumbs from '../Category/Breadcrumbs';
import ProductItem from '../Category/ProductItem';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import Pagination from '../../hocs/Pagination/Pagination';
import Overlay from '../../hocs/Overlay/Overlay';
import { addRecentlyViewed } from '../../actions/checkout';
import { resetFilter, preloadPage } from '../../actions/environment';
import { centimetersToInches } from '../../utils/conversion';
import { addEvent, removeEvent, height, fixBackgroundScroll, hasClass, removeClass } from '../../utils/dom';
import { translate, prefix, removeLanguagePrefix, translateObjectData } from '../../utils/locale';
import { addLists } from '../../utils/react-helpers';
import { urlify } from '../../utils/string';
import { origin } from '../../config';
import s from './Product.scss';

const Product = ({ data, currentPath, setup, productCache, filter, recentlyViewed, loading, pageData, ...props }) => {
	if (loading) return true;
	if (!data || !data.productData || !productCache?.[setup.market]?.[setup.pricelist]) return null;

	// State
	const detailContainer = useRef(null);
	const [currentVariant, switchVariant] = useState('');
	const product = translateObjectData(setup, currentVariant ? pageData[currentVariant].productData : data.productData);
	const soldOut = product.markets[setup.market].stockOfAllItems === 0;
	const sizes = Object.keys(product.items).map((item) => ({ name: product.items[item].name, item, stock: product.items[item].stockByMarket[setup.market] }));

	const [infoSection, setInfoSection] = useState('description');
	const [showingDetail, toggleDetail] = useState(false);
	// const [teasingDetail, toggleTeasingDetail] = useState(false);
	const [collapsingDetail, toggleExitDetail] = useState(false);
	const [stickyDetail, toggleDetailStickiness] = useState(true);
	const [showNotifyMe, toggleNotifyMe] = useState(soldOut);
	const [stickyPurchase, togglePurchaseStickiness] = useState(true);
	const [selectingSize, toggleSelectingSize] = useState(false);
	const [usingCm, toggleCm] = useState(setup.country !== 'US');

	const cache = productCache[setup.market][setup.pricelist];
	const productSections = data.parts || [];
	const productRelations = { variant: [] };

	// Replace data with variant.
	const price = product.markets[setup.market].pricesByPricelist[setup.pricelist];
	const shareLocation = `${origin}${prefix(setup, product.canonicalUri)}`;
	const categoryGender = currentPath.indexOf('woman') !== -1 ? 'woman' : 'man';

	if (product?.relatedProducts) {
		product.relatedProducts.forEach((rP) => {
			if (!productRelations[rP.relation]) productRelations[rP.relation] = [];
			productRelations[rP.relation] = [...productRelations[rP.relation], rP.product];
		});
	}

	const metaInfo = {
		title: product.metaTitle || `${product.name} ${product.variantName}`,
		metaDescription: product.metaDescription,
		linkCanonical: shareLocation,
	};

	const badges = [];

	if (product.pr_badges) {
		Object.keys(product.pr_badges).forEach((badge) => {
			const badgeItem = product.pr_badges[badge];
			if (!!badgeItem.isNew && !price.newProduct) return;
			if (!!badgeItem.isSale && !price.showAsOnSale) return;
			if (!!badgeItem.isSoldOut && !soldOut) return;
			if (badgeItem.image) {
				badges.push(<span key={badge} className={[s.badge, s.badge__svg].join(' ')}><img src={badgeItem.image.url} alt="" /></span>);
			}
		});
	}
	/*
	if (!soldOut && price.newProduct && !newSet) {
		badges.unshift(<span key="new-badge" className={[s.badge, s.badge__new].join(' ')}>{ translate(setup, 'new') }</span>);
	} else if (!soldOut && price.showAsOnSale && !saleSet) {
		badges.unshift(<span key="sale-badge" className={[s.badge, s.badge__sale].join(' ')}>-{ discount }%</span>);
	}
	*/

	const toggleInfoSection = (e) => {
		const value = e.currentTarget.value;
		if (value === 'size-guide') fixBackgroundScroll(true);
		setInfoSection((prevState) => (prevState === value ? '' : value));
	};

	const toggleMeasurement = (e) => {
		const value = e.currentTarget.value;
		toggleCm(value === 'cm');
	};

	const variantClickHandler = (e) => {
		const location = removeLanguagePrefix(e.currentTarget.getAttribute('href'));
		if (pageData[location]) {
			e.preventDefault();
			window.history.replaceState({}, '', prefix(setup, location));
			switchVariant(location);
		}
	};

	const toggleDetailHandler = () => {
		toggleDetail((prevState) => {
			if (prevState) {
				toggleExitDetail(true);
				setTimeout(() => {
					toggleExitDetail(false);
				}, 400);
			}
			return !prevState;
		});
	};

	/*
	const touchStartOpenDetailHandler = (initialEvent) => {
		const startPosition = initialEvent.touches[0].clientY;
		const $root = document.getElementById('root');
		let runFirst = true;

		const dragDetailHandler = (e) => {
			const currentPosition = e.touches[0].clientY;
			if (currentPosition < startPosition - 50) {
				removeEvent($root, 'touchmove.detailOpenToggle');
				removeEvent($root, 'touchend.detailOpenToggle');
				toggleDetail(true);
				toggleTeasingDetail(false);
			}
			if (runFirst) {
				toggleTeasingDetail(true);
			}
			runFirst = false;
		};

		addEvent($root, 'touchmove.detailOpenToggle', (e) => {
			e.preventDefault();
			e.stopPropagation();
			window.requestAnimationFrame(() => {
				dragDetailHandler(e);
			});
		}, { passive: false });

		addEvent($root, 'touchend.detailOpenToggle', () => {
			removeEvent($root, 'touchmove.detailOpenToggle', { passive: false });
			removeEvent($root, 'touchend.detailOpenToggle');
			toggleTeasingDetail(false);
		});
	};

	const touchStartCloseDetailHandler = (initialEvent) => {
		const startPosition = initialEvent.touches[0].clientY;
		const $root = document.getElementById('root');
		let runFirst = true;

		const dragDetailHandler = (e) => {
			const currentPosition = e.touches[0].clientY;
			if (currentPosition > startPosition + 50) {
				removeEvent($root, 'touchmove.detailCloseToggle');
				removeEvent($root, 'touchend.detailCloseToggle');
				toggleDetailHandler();
				toggleTeasingDetail(false);
			}
			if (runFirst) {
				toggleTeasingDetail(true);
			}
			runFirst = false;
		};

		addEvent($root, 'touchmove.detailCloseToggle', (e) => {
			e.preventDefault();
			e.stopPropagation();
			window.requestAnimationFrame(() => {
				dragDetailHandler(e);
			});
		}, { passive: false });

		addEvent($root, 'touchend.detailCloseToggle', () => {
			removeEvent($root, 'touchmove.detailCloseToggle', { passive: false });
			removeEvent($root, 'touchend.detailCloseToggle');
			toggleTeasingDetail(false);
		});
	};
	*/

	const closeSelectingSizeHandler = () => {
		toggleSelectingSize(false);
		if (hasClass(document.body, 'u_addedToBag')) {
			removeClass(document.body, 'u_addedToBag');
		}
	};

	const closeOverlayHandler = () => {
		fixBackgroundScroll(false);
		setInfoSection('description');
	};

	const variants = [{ wash: product.variant_wash_swatch || {}, selected: true, variantName: product.variantName }, ...productRelations.variant.map((item) => (
		{
			wash: cache.products?.[item]?.variant_wash_swatch || {}, // eslint-disable-line camelcase
			uri: cache.products?.[item]?.uri,
			variantName: cache.products?.[item]?.variantName,
		}
	))]
		.filter((a) => a.wash.image)
		.sort((a, b) => {
			if (!a.wash.order || !b.wash.order || a.wash.order === b.wash.order) return a.variantName.localeCompare(b.variantName);
			return (parseInt(a.wash.order, 10) > parseInt(b.wash.order, 10) ? 1 : -1);
		});

	const [analytics, analyticsShown] = useState(false);
	useEffect(() => {
		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		if (analytics !== currentPath) {
			props.addRecentlyViewed(product.product);
			viewAnalytics(price, product, setup, currentPath);
			analyticsShown(currentPath);

			const preloadProductPaths = variants.filter((i) => !!i.uri).map((item) => prefix(setup, `${currentPath.slice(0, currentPath.lastIndexOf('/'))}/${item.uri}`).slice(1));
			props.preloadPage(preloadProductPaths);
		}
	}, [currentPath, product, price, setup, analytics]);

	useEffect(() => {
		let isSticky = true;
		let isPurchaseSticky = true;
		let containerHeight = 1000; // Images and details
		let detailHeight = 200; // Sticky purchase height

		if (detailContainer.current) {
			containerHeight = height(detailContainer.current.parentElement.parentElement);
			detailHeight = height(detailContainer.current.children[0]); // 50 for the menu, 21 for the bit that sticks up.
		}

		const updateStickiness = () => {
			if (!detailContainer.current) return;
			const top = window.pageYOffset || document.documentElement.scrollTop;

			if (containerHeight > window.innerHeight && top + window.innerHeight >= containerHeight + 50 && isPurchaseSticky) {
				togglePurchaseStickiness(false);
				isPurchaseSticky = false;
			} else if (top + window.innerHeight < containerHeight + 50 && !isPurchaseSticky) {
				togglePurchaseStickiness(true);
				isPurchaseSticky = true;
			}

			if (containerHeight > window.innerHeight && top + window.innerHeight >= containerHeight - detailHeight && isSticky) {
				toggleDetailStickiness(false);
				toggleDetail((prevState) => {
					if (prevState) {
						toggleExitDetail(true);
						setTimeout(() => {
							toggleExitDetail(false);
						}, 400);
					}
					return false;
				});
				isSticky = false;
			} else if (top + window.innerHeight < containerHeight - detailHeight && !isSticky) {
				toggleDetailStickiness(true);
				isSticky = true;
			}

			containerHeight = height(detailContainer.current.parentElement.parentElement);
			detailHeight = height(detailContainer.current.children[0]);
		};

		updateStickiness();

		addEvent(window, 'scroll.removeSticky', () => {
			window.requestAnimationFrame(updateStickiness);
		});

		return () => {
			removeEvent(window, 'scroll.removeSticky');
		};
	}, [currentPath, product]);

	return (
		<main role="main">
			<Top setup={setup} pageContent={metaInfo} paths={data.paths} />
			<div className={s.productWrapper}>
				{
					badges.length > 0 && (
						<div className={s.badges}>
							{ badges }
						</div>
					)
				}
				<div className={[s.productPurchase, (!stickyPurchase ? s.productPurchase__static : null)].join(' ')}/* onTouchStart={touchStartOpenDetailHandler} */>
					{ selectingSize && <button className={s.productPurchase_screen} aria-label="Close size chooser" onClick={closeSelectingSizeHandler} type="button" /> }
					<Purchase setup={setup} currentPath={currentPath} productId={product.product} product={product} extraProducts={productRelations.bundle} sizes={sizes} price={price} soldOut={soldOut ? translate(setup, 'soldOut') : false} comingSoon={product.variant_coming_soon_bool} toggleInfoSection={toggleInfoSection} selectingSize={selectingSize} toggleSelectingSize={toggleSelectingSize} showNotifyMe={showNotifyMe} toggleNotifyMe={toggleNotifyMe} />
				</div>
				<div id="productTop" className={s.product}>
					<ErrorBoundary message="Product details missing data" debugData={product}>
						{ showingDetail && <button className={s.product_detailScreen} aria-label="Close product information" type="button" onClick={toggleDetailHandler} /> }
						<div className={[s.productDetail, (selectingSize ? s.productDetail__darker : null),/* (teasingDetail ? s.productDetail__tease : null), */(showingDetail ? s.productDetail__expanded : null), (collapsingDetail ? s.productDetail__collapsing : null), (!stickyDetail ? s.productDetail__static : null)].join(' ')} ref={detailContainer}>
							<div className={s.productDetail_inner}>
								{/* <button className={s.productDetail_toggle} type="button" aria-label="Open Details" onClick={toggleDetailHandler} onTouchStart={touchStartCloseDetailHandler}><span /></button> */}
								<div className={s.productDetail_breadcrumbs}/* onTouchStart={touchStartCloseDetailHandler} */>
									<Breadcrumbs currentPath={currentPath} filter={filter} filtering={cache} data={data} setup={setup} product={product.variantName} resetFilter={props.resetFilter} />
								</div>
								<div className={s.productDetail_content}>
									<h1 className={s.productDetail_content_title}>{ product.name }</h1>
									{
										variants && variants.length > 1 && (
											<ErrorBoundary message="Related Product details missing data" debugData={productRelations}>
												<div className={s.productDetail_content_variants}>
													<div className={s.productDetail_content_variants_title}>{ product.variantName }</div>
													{
														variants.map((item) => {
															const VariantTag = item.uri ? Link : 'span';
															const attrs = {};
															if (item.uri) {
																attrs.to = prefix(setup, `${currentPath.slice(0, currentPath.lastIndexOf('/'))}/${item.uri}`);
																attrs.onClick = variantClickHandler;
															}
															attrs['data-title'] = item.variantName;
															return (
																<VariantTag key={item.uri} className={[s.productDetail_content_variant, (item.selected ? s.productDetail_content_variant__selected : null)].join(' ')} {...attrs}>
																	{
																		item.wash.image && (
																			<span key={item.wash.image.url} className={s.productDetail_content_variant_swatch} style={{ backgroundImage: `url(${item.wash.image.url})` }} />
																		)
																	}
																	{
																		!item.wash.image && (
																			<span key={item.wash.text} className={s.productDetail_content_variant_swatch} style={{ backgroundColor: '#000' }} />
																		)
																	}
																</VariantTag>
															);
														})
													}
												</div>
											</ErrorBoundary>
										)
									}
									<div className={s.productDetail_content_infoSections}>
										<div className={s.productDetail_content_infoSection}>
											<button className={[s.productDetail_content_infoSection_menuItem, (infoSection === 'description' ? s.productDetail_content_infoSection_menuItem__selected : null)].join(' ')} value="description" type="button" onClick={toggleInfoSection}>
												<svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
													<path d="M6 1L11 6" stroke="black" strokeLinecap="square" strokeLinejoin="round" />
													<path d="M1 6L6 1" stroke="black" strokeLinecap="square" strokeLinejoin="round" />
												</svg>
												<span>{ translate(setup, 'productDescription') }</span>
											</button>
											<div className={[s.productDetail_content_infoSection_item, (infoSection === 'description' || infoSection === 'size-guide' ? s.productDetail_content_infoSection_item__selected : null)].join(' ')}>
												{ product.variant_description && addLists(product.variant_description) }
											</div>
										</div>
										{
											product.variant_details && (
												<div className={s.productDetail_content_infoSection}>
													<button className={[s.productDetail_content_infoSection_menuItem, (infoSection === 'details' ? s.productDetail_content_infoSection_menuItem__selected : null)].join(' ')} value="details" type="button" onClick={toggleInfoSection}>
														<svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
															<path d="M6 1L11 6" stroke="black" strokeLinecap="square" strokeLinejoin="round" />
															<path d="M1 6L6 1" stroke="black" strokeLinecap="square" strokeLinejoin="round" />
														</svg>
														<span>{ translate(setup, 'productDetails') }</span>
													</button>
													<div className={[s.productDetail_content_infoSection_item, (infoSection === 'details' ? s.productDetail_content_infoSection_item__selected : null)].join(' ')}>
														{ product.variant_details && addLists(product.variant_details) }
													</div>
												</div>
											)
										}
										{/*
											product.variant_size_information && (
												<div className={s.productDetail_content_infoSection}>
													<button className={[s.productDetail_content_infoSection_menuItem, (infoSection === 'fit' ? s.productDetail_content_infoSection_menuItem__selected : null)].join(' ')} value="fit" type="button" onClick={toggleInfoSection}>
														<svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
															<path d="M6 1L11 6" stroke="black" strokeLinecap="square" strokeLinejoin="round" />
															<path d="M1 6L6 1" stroke="black" strokeLinecap="square" strokeLinejoin="round" />
														</svg>
														<span>{ translate(setup, 'productFit') }</span>
													</button>
													<div className={[s.productDetail_content_infoSection_item, (infoSection === 'fit' ? s.productDetail_content_infoSection_item__selected : null)].join(' ')}>
														{ product.variant_size_information && addLists(product.variant_size_information) }
													</div>
												</div>
											)
										*/}
										{
											product.shipping_information && (
												<div className={s.productDetail_content_infoSection}>
													<button className={[s.productDetail_content_infoSection_menuItem, (infoSection === 'shipping' ? s.productDetail_content_infoSection_menuItem__selected : null)].join(' ')} value="shipping" type="button" onClick={toggleInfoSection}>
														<svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
															<path d="M6 1L11 6" stroke="black" strokeLinecap="square" strokeLinejoin="round" />
															<path d="M1 6L6 1" stroke="black" strokeLinecap="square" strokeLinejoin="round" />
														</svg>
														<span>{ translate(setup, 'productShipping') }</span>
													</button>
													<div className={[s.productDetail_content_infoSection_item, (infoSection === 'shipping' ? s.productDetail_content_infoSection_item__selected : null)].join(' ')}>
														{ product.shipping_information && addLists(product.shipping_information) }
													</div>
												</div>
											)
										}
										{
											product.pr_sustainability && (
												<div className={s.productDetail_content_infoSection}>
													<button className={[s.productDetail_content_infoSection_menuItem, (infoSection === 'sustainability' ? s.productDetail_content_infoSection_menuItem__selected : null)].join(' ')} value="sustainability" type="button" onClick={toggleInfoSection}>
														<svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
															<path d="M6 1L11 6" stroke="black" strokeLinecap="square" strokeLinejoin="round" />
															<path d="M1 6L6 1" stroke="black" strokeLinecap="square" strokeLinejoin="round" />
														</svg>
														<span>{ translate(setup, 'productSustainability') }</span>
													</button>
													<div className={[s.productDetail_content_infoSection_item, (infoSection === 'sustainability' ? s.productDetail_content_infoSection_item__selected : null)].join(' ')}>
														{ product.pr_sustainability && addLists(product.pr_sustainability) }
													</div>
												</div>
											)
										}
										{
											product.measurementChartRows && Object.keys(product.measurementChartRows).length > 0 && (
												<div className={s.productDetail_content_infoSection}>
													<button className={[s.productDetail_content_infoSection_menuItem, s.productDetail_content_infoSection_menuItem__sizeGuide, (infoSection === 'size-guide' ? s.productDetail_content_infoSection_menuItem__selected : null)].join(' ')} value="size-guide" type="button" onClick={toggleInfoSection}>
														<svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
															<path d="M6 1L11 6" stroke="black" strokeLinecap="square" strokeLinejoin="round" />
															<path d="M1 6L6 1" stroke="black" strokeLinecap="square" strokeLinejoin="round" />
														</svg>
														<span>{ translate(setup, 'productSizeGuide') }</span>
													</button>
												</div>
											)
										}
									</div>
								</div>
							</div>
						</div>
					</ErrorBoundary>
					<ErrorBoundary message="Product images missing data" debugData={product}>
						<ProductImages product={product} categoryGender={categoryGender} />
					</ErrorBoundary>
				</div>
			</div>
			{
				product.measurementChartRows && Object.keys(product.measurementChartRows).length > 0 && (
					<Overlay isShowing={infoSection === 'size-guide'} closeOverlayHandler={closeOverlayHandler}>
						<div id={`size-chart-product-id-${product.groupId}`} className={s.sizeChart}>
							<h5 className={s.sizeChart_title}>{ translate(setup, 'sizeGuideHeading') }</h5>
							<p className={s.sizeChart_product}>{ product.name }</p>
							<div className={s.sizeChart_measurementConverter}>
								<span>{ `${translate(setup, 'sizeGuideMeasurements')} ` }</span>
								<button className={[s.sizeChart_measurementConverter_button, (usingCm ? s.sizeChart_measurementConverter_button__selected : null)].join(' ')} type="button" onClick={toggleMeasurement} value="cm">{ translate(setup, 'centimeters') }</button>
								<span> / </span>
								<button className={[s.sizeChart_measurementConverter_button, (usingCm ? null : s.sizeChart_measurementConverter_button__selected)].join(' ')} type="button" onClick={toggleMeasurement} value="inches">{ translate(setup, 'inches') }</button>
							</div>
							<div className={s.sizeChart_table}>
								<div className={s.sizeChart_table_overflow}>
									<table>
										<thead>
											<tr>
												<th>{ translate(setup, 'sizeGuideSize') }</th>
												{
													Object.keys(product.measurementChartRows).map((key) => <th key={key}>{ key }</th>)
												}
											</tr>
										</thead>
										<tbody>
											{
												Object.keys(product.measurementChartRows[Object.keys(product.measurementChartRows)[0]]).map((key) => (
													<tr key={key}>
														<th>{ key }</th>
														{
															Object.keys(product.measurementChartRows).map((m) => {
																if (!usingCm) { // /\(cm\)/.test(key) &&
																	const { wholeInches, numerator = 0, denominator = 4 } = centimetersToInches(product.measurementChartRows[m][key].replace(',', '.'), 4);
																	return (
																		<td key={urlify(`${m}-${key}`)}>
																			<span>{wholeInches}</span>
																			{ numerator > 0 && <span>{`${numerator}/${denominator}`}</span> }
																		</td>
																	);
																}
																return <td key={urlify(`${m}-${key}`)}>{ product.measurementChartRows[m][key] }</td>;
															})
														}
													</tr>
												))
											}
										</tbody>
									</table>
								</div>
							</div>
							{
								product.variant_size_information && (
									<div className={s.sizeChart_productDetails}>
										{ addLists(product.variant_size_information) }
									</div>
								)
							}
						</div>
					</Overlay>
				)
			}
			{
				productRelations.standard && productRelations.standard.length > 0 && (
					<ErrorBoundary message="Related Product details missing data" debugData={productRelations.standard}>
						<div className={s.productRelations}>
							<h2 className={s.productRelations_title}>{ translate(setup, 'productRelated') }</h2>
							<div className={s.productRelations_inner}>
								<Pagination ListComponent={ProductItem} variantClasses={['productItem__perRow_4']} list={productRelations.standard.slice(0, 4)} cache={cache.products} currentPath={currentPath} currentView="related" canonical />
							</div>
						</div>
					</ErrorBoundary>
				)
			}
			{
				productSections.length > 0 && (
					<div className={s.productCMS}>
						{
							productSections.map((section) => (
								<TemplateParser sections={section.sections} setup={setup} isProduct />
							))
						}
					</div>
				)
			}
		</main>
	);
};

const mapState = ({ environment, productCache, checkout, filter, pageStatus, pageData }, { data, currentPath }) => ({
	data,
	currentPath,
	header: environment.header,
	setup: environment.setup,
	productCache,
	recentlyViewed: checkout.recentlyViewed,
	filter,
	loading: pageStatus.loading,
	pageData,
});

export default hot(module)(connect(mapState, { resetFilter, addRecentlyViewed, preloadPage })(Product));
